import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';

import { useDebounce } from '../../shared/hooks';
import { useField } from '@unform/core';
import { BensService } from '../services/api/servicos/BensService';

type TAutoCompleteOption = {
  idbem: string;
  label: string;
}

interface IAutoCompleteBemDoParceiroProps {
  isExternalLoading?: boolean;
  idparceiro?: string;
  // Diferente de AutoCompleteViculos — a tela de Nova OS precisa saber qual
  // bem foi escolhido (pra buscar o resumo dele e decidir se mostra
  // KM ou horímetro), não só o unform guardar o valor internamente.
  aoSelecionar?: (idbem: string | undefined) => void;
}

// Espelha AutoCompleteViculos.tsx (idtransportadora -> idveiculo): aqui é
// idparceiro -> idbem. Reseta a seleção sempre que o parceiro muda, pra não
// deixar um bem de outro cliente escondido no campo.
export const AutoCompleteBemDoParceiro: React.FC<IAutoCompleteBemDoParceiroProps> = ({ isExternalLoading = false, idparceiro = '', aoSelecionar }) => {

  const { fieldName, registerField, defaultValue, error, clearError } = useField('idbem');

  const { debounce } = useDebounce();

  const [selectedId, setSelectedId] = useState<string | undefined>(defaultValue);

  const [opcoes, setOpcoes] = useState<TAutoCompleteOption[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [busca, setBusca] = useState('');

  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => selectedId,
      setValue: (_, newSelectedId) => setSelectedId(newSelectedId),
    });
  }, [registerField, fieldName, selectedId]);

  useEffect(() => {
    setSelectedId(undefined);
    setBusca('');
  }, [idparceiro]);

  useEffect(() => {
    aoSelecionar?.(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    if (!idparceiro) {
      setOpcoes([]);
      return;
    }
    setIsLoading(true);
    debounce(() => {
      BensService.getAllList(idparceiro, busca, selectedId)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            // silencioso, igual AutoCompleteViculos
          } else {
            setOpcoes(result.data.map(bem => ({ idbem: bem.idbem, label: bem.descricao })));
          }
        });
    });
  }, [idparceiro, busca, selectedId]);

  const autoCompleteSelectedOption = useMemo(() => {
    if (!selectedId) return null;

    const selectedOption = opcoes.find(opcao => opcao.idbem === selectedId);
    if (!selectedOption) return null;

    return selectedOption;
  }, [selectedId, opcoes]);

  return (
    <Autocomplete
      openText='Abrir'
      closeText='Fechar'
      noOptionsText={idparceiro ? 'Sem opções' : 'Selecione um parceiro primeiro'}
      loadingText='Carregando...'

      disablePortal

      options={opcoes}
      loading={isLoading}
      disabled={isExternalLoading || !idparceiro}
      value={autoCompleteSelectedOption}
      onInputChange={(_, newValue, reason) => {
        if (reason === 'input') setBusca(newValue);
      }}
      onChange={(_, newValue) => { setSelectedId(newValue?.idbem); setBusca(''); clearError(); }}
      popupIcon={(isLoading) ? <CircularProgress size={28} /> : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          size='small'
          label="Bem (veículo/máquina/patrimônio)"
          error={!!error}
          helperText={error}
        />
      )}
    />
  );
};
